import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DOMPurify from 'dompurify';
import { 
  getCategories, getTags, createArticle, updateArticle, getArticle, 
  aiSummarize, aiTranslate, uploadFile, autoSaveArticle, createDraft,
  scheduleArticle, unpublishArticle, getMediaLibrary, createTag
} from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  ArrowLeft, Save, Send, Sparkles, Languages, Loader2, Eye, Bold, Italic, 
  List, ListOrdered, Link2, Image, Quote, Heading1, Heading2, Upload, Code, 
  X, Calendar, Clock, Globe, FileText, ImagePlus, Trash2, Check, AlertCircle,
  Type, AlignLeft, Minus, Plus, Underline
} from 'lucide-react';

// --- Font options ---
const FONT_OPTIONS = [
  { label: 'Baloo Tamma 2', value: "'Baloo Tamma 2', sans-serif" },
  { label: 'Noto Sans Kannada', value: "'Noto Sans Kannada', sans-serif" },
  { label: 'Noto Serif Kannada', value: "'Noto Serif Kannada', serif" },
  { label: 'Tiro Kannada', value: "'Tiro Kannada', serif" },
  { label: 'Inter', value: "'Inter', sans-serif" },
  { label: 'Playfair Display', value: "'Playfair Display', serif" },
];

// --- Image Uploader with Drag & Drop ---
function ImageUploader({ onUpload, currentImage, onRemove, captionValue, onCaptionChange }) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || '');
  const fileInputRef = useRef(null);

  useEffect(() => { setPreview(currentImage || ''); }, [currentImage]);

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) await doUpload(file);
  };

  const doUpload = async (file) => {
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
      const res = await uploadFile(file);
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setPreview(url);
      onUpload(url);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      alert('Upload failed: ' + detail);
    }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-3">
      {preview ? (
        <div className="relative rounded-xl overflow-hidden group">
          <img src={preview} alt="Preview" className="w-full h-48 object-cover" />
          {uploading && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
          )}
          <div className="absolute top-2 right-2 flex gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-1.5 rounded-full bg-white/95 text-[#1A1A1A] text-xs font-bold hover:bg-white shadow flex items-center gap-1"
              data-testid="change-image-btn"
              disabled={uploading}
              title="Replace image"
            >
              <Upload className="w-3.5 h-3.5" /> Change
            </button>
            <button
              type="button"
              onClick={() => { setPreview(''); onRemove(); }}
              className="p-1.5 rounded-full bg-red-500 text-white hover:bg-red-600 shadow"
              data-testid="remove-image-btn"
              disabled={uploading}
              title="Remove image"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${isDragging ? 'border-red-500 bg-palace-500/10' : 'border-white/20 hover:border-white/40'}`}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 text-[#D93025] animate-spin" />
              <p className="text-sm text-gray-500">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <ImagePlus className="w-10 h-10 text-[#737373]" />
              <p className="text-sm text-gray-500">Drag & drop or <span className="text-[#D93025]">click to browse</span></p>
              <p className="text-xs text-[#737373]">PNG, JPG, GIF, WebP, AVIF, HEIC up to 25MB</p>
            </div>
          )}
        </div>
      )}
      {preview && onCaptionChange && (
        <Input value={captionValue || ''} onChange={(e) => onCaptionChange(e.target.value)}
          placeholder="Photo caption (optional)" className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] text-sm placeholder:text-[#737373]" data-testid="photo-caption-input" />
      )}
      <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => { if (e.target.files[0]) doUpload(e.target.files[0]); e.target.value = ''; }} className="hidden" />
    </div>
  );
}

// --- Media Library Modal (with Upload tab) ---
function MediaLibraryModal({ isOpen, onClose, onSelect }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('library');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadMedia = () => {
    setLoading(true);
    getMediaLibrary({ limit: 100 }).then(res => setMedia(res.data.media || [])).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isOpen) {
      setTab('library');
      loadMedia();
    }
  }, [isOpen]);

  const handleUpload = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setUploading(true);
    const uploadedUrls = [];
    let failed = 0;
    for (const file of list) {
      try {
        const res = await uploadFile(file);
        uploadedUrls.push(`${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
      } catch (err) {
        failed += 1;
        console.error('Upload failed for', file?.name, err);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
    if (uploadedUrls.length === 0) {
      alert('All uploads failed');
      return;
    }
    if (failed > 0) {
      alert(`${failed} of ${list.length} uploads failed.`);
    }
    if (uploadedUrls.length === 1) {
      // Single file → auto-insert + close (preserves original UX)
      onSelect(uploadedUrls[0]);
      onClose();
    } else {
      // Multi-file → insert all and refresh library
      onSelect(uploadedUrls);
      setTab('library');
      loadMedia();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="media-library-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-4xl max-h-[80vh] bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-bold text-[#1A1A1A]">Media</h3>
            <div className="flex items-center gap-1 bg-[#F5F5F0] rounded-lg p-0.5" role="tablist">
              <button
                type="button"
                onClick={() => setTab('library')}
                className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${tab === 'library' ? 'bg-white text-[#D93025] shadow-sm' : 'text-[#737373]'}`}
                data-testid="media-tab-library"
              >Library</button>
              <button
                type="button"
                onClick={() => setTab('upload')}
                className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${tab === 'upload' ? 'bg-white text-[#D93025] shadow-sm' : 'text-[#737373]'}`}
                data-testid="media-tab-upload"
              >Upload New</button>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-[#F5F5F0]"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {tab === 'upload' ? (
            <div
              className="border-2 border-dashed border-[#EAEAE8] rounded-2xl p-10 text-center cursor-pointer hover:border-[#D93025] transition-colors"
              onClick={() => !uploading && fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.length) handleUpload(e.dataTransfer.files); }}
              data-testid="media-upload-dropzone"
            >
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-10 h-10 text-[#D93025] animate-spin" />
                  <p className="text-sm text-[#737373]">Uploading...</p>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 mx-auto mb-2 text-[#737373]" />
                  <p className="text-sm font-bold text-[#1A1A1A]">Click to upload or drag & drop</p>
                  <p className="text-xs text-[#737373] mt-1">PNG, JPG, WebP up to 20 MB · Select multiple files</p>
                  <p className="text-[11px] text-[#737373] mt-3">Recommended: Featured 1600×900 · Inline 1200×675 · Thumb 600×400</p>
                </>
              )}
              <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => handleUpload(e.target.files)} data-testid="media-upload-input" />
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-[#D93025] animate-spin" /></div>
          ) : media.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#737373] mb-3">No media files yet</p>
              <Button size="sm" onClick={() => setTab('upload')} className="bg-[#D93025] text-white"><Upload className="w-3 h-3 mr-1" />Upload first image</Button>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-3">
              {media.map(item => (
                <button key={item.media_id} type="button" onClick={() => { onSelect(`${process.env.REACT_APP_BACKEND_URL}${item.url}`); onClose(); }}
                  className="relative group rounded-lg overflow-hidden border border-gray-200 hover:border-red-500 transition-colors" data-testid={`media-item-${item.media_id}`}>
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${item.url}`} alt={item.original_name} className="w-full h-24 object-cover" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Check className="w-6 h-6 text-white" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Rich Text Editor with Font/Size/LineHeight Controls ---
function RichTextEditor({ value, onChange, placeholder, onImageInsert }) {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [fontSize, setFontSize] = useState(16);
  const [lineHeight, setLineHeight] = useState(1.8);
  const [selectedFont, setSelectedFont] = useState("'Baloo Tamma 2', sans-serif");

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = DOMPurify.sanitize(value || '');
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const execCommand = (command, val = null) => {
    document.execCommand(command, false, val);
    editorRef.current?.focus();
    handleInput();
  };

  const applyFontSize = (size) => {
    setFontSize(size);
    // Use fontSize command with 7 levels then override with CSS
    const sel = window.getSelection();
    if (sel.rangeCount > 0 && !sel.isCollapsed) {
      document.execCommand('fontSize', false, '7');
      const fonts = editorRef.current?.querySelectorAll('font[size="7"]');
      if (fonts) {
        fonts.forEach(f => {
          f.removeAttribute('size');
          f.style.fontSize = `${size}px`;
        });
      }
      handleInput();
    }
  };

  const applyLineHeight = (lh) => {
    setLineHeight(lh);
    if (editorRef.current) {
      editorRef.current.style.lineHeight = String(lh);
    }
  };

  const applyFont = (font) => {
    setSelectedFont(font);
    const sel = window.getSelection();
    if (sel.rangeCount > 0 && !sel.isCollapsed) {
      document.execCommand('fontName', false, font.replace(/'/g, ''));
      handleInput();
    }
  };

  const insertImage = () => {
    if (onImageInsert) onImageInsert();
    else {
      const url = prompt('Enter image URL:');
      if (url) execCommand('insertImage', url);
    }
  };

  const urlToEmbed = (url) => {
    try {
      const u = new URL(url);
      const h = u.hostname.replace('www.', '');
      if (h.includes('youtube.com') || h.includes('youtu.be')) {
        const vid = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([a-zA-Z0-9_-]{6,})/)?.[1];
        if (vid) return `<div class="embed-youtube" style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:1em 0;border-radius:12px"><iframe src="https://www.youtube.com/embed/${vid}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;border-radius:12px" allowfullscreen loading="lazy"></iframe></div>`;
      }
      if (h.includes('twitter.com') || h.includes('x.com')) {
        // Twitter oEmbed blockquote renders nicely if widgets.js is available; fallback is a styled card.
        return `<blockquote class="twitter-tweet" data-dnt="true"><a href="${url}">${url}</a></blockquote>`;
      }
      if (h.includes('instagram.com')) {
        return `<blockquote class="instagram-media" data-instgrm-permalink="${url}" data-instgrm-version="14" style="background:#FFF;border:1px solid #EAEAE8;border-radius:12px;margin:1em 0;padding:16px"><a href="${url}">${url}</a></blockquote>`;
      }
      if (h.includes('spotify.com')) {
        const embedUrl = url.replace('open.spotify.com/', 'open.spotify.com/embed/');
        return `<iframe src="${embedUrl}" width="100%" height="232" style="border-radius:12px;margin:1em 0" frameborder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy"></iframe>`;
      }
      if (h.includes('soundcloud.com')) {
        return `<iframe width="100%" height="166" scrolling="no" frameborder="no" style="margin:1em 0" src="https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}"></iframe>`;
      }
    } catch { /* ignore */ }
    return null;
  };

  const handlePaste = (e) => {
    // Prefer HTML? Not for our editor — we strip to plain text, then auto-embed URLs.
    const clip = e.clipboardData;
    const text = clip.getData('text/plain') || '';
    const trimmed = text.trim();
    const isOnlyUrl = /^https?:\/\/\S+$/i.test(trimmed);
    if (isOnlyUrl) {
      const embed = urlToEmbed(trimmed);
      if (embed) {
        e.preventDefault();
        document.execCommand('insertHTML', false, embed);
        // Load Twitter/Instagram widgets so pasted blockquotes render as real posts.
        loadSocialEmbedScripts(trimmed);
        handleInput();
        return;
      }
      // Non-embeddable URL → insert as clickable link + preview card.
      e.preventDefault();
      const card = `<a href="${trimmed}" target="_blank" rel="noopener" style="display:inline-block;margin:1em 0;padding:10px 14px;border:1px solid #EAEAE8;border-radius:10px;background:#FAFAF8;color:#D93025;text-decoration:none;font-weight:600">${trimmed}</a>`;
      document.execCommand('insertHTML', false, card);
      handleInput();
      return;
    }
    e.preventDefault();
    document.execCommand('insertText', false, text);
    handleInput();
  };

  const loadSocialEmbedScripts = (url) => {
    try {
      if (/twitter\.com|x\.com/i.test(url) && !document.getElementById('twitter-wjs')) {
        const s = document.createElement('script');
        s.id = 'twitter-wjs'; s.async = true; s.src = 'https://platform.twitter.com/widgets.js';
        document.body.appendChild(s);
      } else if (window.twttr?.widgets) {
        window.twttr.widgets.load();
      }
      if (/instagram\.com/i.test(url) && !document.getElementById('instagram-embed-js')) {
        const s = document.createElement('script');
        s.id = 'instagram-embed-js'; s.async = true; s.src = 'https://www.instagram.com/embed.js';
        document.body.appendChild(s);
      } else if (window.instgrm?.Embeds) {
        window.instgrm.Embeds.process();
      }
    } catch { /* ignore */ }
  };

  const insertEmbed = () => {
    const url = prompt('Enter embed URL (YouTube, Twitter, etc.):');
    if (!url) return;
    let html = '';
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const vid = url.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]+)/)?.[1];
      if (vid) html = `<div style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:1em 0"><iframe src="https://www.youtube.com/embed/${vid}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0" allowfullscreen></iframe></div>`;
    } else if (url.includes('twitter.com') || url.includes('x.com')) {
      html = `<blockquote class="twitter-tweet" data-dnt="true"><a href="${url}">${url}</a></blockquote>`;
      loadSocialEmbedScripts(url);
    }
    if (!html) html = `<a href="${url}" target="_blank">${url}</a>`;
    document.execCommand('insertHTML', false, html);
    handleInput();
  };

  const insertIframe = () => {
    const code = prompt('Paste iframe / embed HTML code (Twitter, YouTube, Instagram, Facebook, etc.):');
    if (!code) return;
    // Allow only iframe / blockquote / script tags - keep as-is
    document.execCommand('insertHTML', false, code);
    // Try to load helper scripts for popular platforms
    if (/twitter\.com|x\.com|twitter-tweet/i.test(code)) loadSocialEmbedScripts('https://twitter.com');
    if (/instagram\.com|instagram-media/i.test(code)) loadSocialEmbedScripts('https://instagram.com');
    handleInput();
  };

  const setImageAlignment = (align) => {
    // Apply align-left|center|right + size-* classes to the currently selected/focused image.
    const sel = window.getSelection();
    let img = null;
    if (sel?.anchorNode) {
      const node = sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement;
      img = node?.tagName === 'IMG' ? node : node?.querySelector?.('img');
    }
    if (!img && editorRef.current) {
      // fall back to last image in editor
      const imgs = editorRef.current.querySelectorAll('img');
      img = imgs[imgs.length - 1];
    }
    if (!img) {
      alert('Click on an image first to align it.');
      return;
    }
    img.classList.remove('align-left', 'align-center', 'align-right');
    img.classList.add(`align-${align}`);
    handleInput();
  };

  const setImageSize = (size) => {
    const imgs = editorRef.current?.querySelectorAll('img');
    const img = imgs?.[imgs.length - 1];
    if (!img) { alert('Insert an image first.'); return; }
    img.classList.remove('size-small', 'size-medium', 'size-large');
    img.classList.add(`size-${size}`);
    handleInput();
  };

  return (
    <div className={`border rounded-xl overflow-hidden transition-colors ${isFocused ? 'border-red-500' : 'border-gray-200'}`} data-testid="rich-text-editor">
      {/* Toolbar Row 1: Formatting */}
      <div className="flex flex-wrap items-center gap-1 p-2 bg-gray-50 border-b border-gray-200">
        <button type="button" onClick={() => execCommand('formatBlock', 'h1')} className="p-2 rounded hover:bg-[#F5F5F0] text-[#4A4A4A] hover:text-[#1A1A1A]" title="Heading 1"><Heading1 className="w-4 h-4" /></button>
        <button type="button" onClick={() => execCommand('formatBlock', 'h2')} className="p-2 rounded hover:bg-[#F5F5F0] text-[#4A4A4A] hover:text-[#1A1A1A]" title="Heading 2"><Heading2 className="w-4 h-4" /></button>
        <div className="w-px h-6 bg-white/10 mx-1" />
        <button type="button" onClick={() => execCommand('bold')} className="p-2 rounded hover:bg-[#F5F5F0] text-[#4A4A4A] hover:text-[#1A1A1A]" title="Bold"><Bold className="w-4 h-4" /></button>
        <button type="button" onClick={() => execCommand('italic')} className="p-2 rounded hover:bg-[#F5F5F0] text-[#4A4A4A] hover:text-[#1A1A1A]" title="Italic"><Italic className="w-4 h-4" /></button>
        <button type="button" onClick={() => execCommand('underline')} className="p-2 rounded hover:bg-[#F5F5F0] text-[#4A4A4A] hover:text-[#1A1A1A]" title="Underline"><Underline className="w-4 h-4" /></button>
        <div className="w-px h-6 bg-white/10 mx-1" />
        <button type="button" onClick={() => execCommand('insertUnorderedList')} className="p-2 rounded hover:bg-[#F5F5F0] text-[#4A4A4A] hover:text-[#1A1A1A]" title="Bullet List"><List className="w-4 h-4" /></button>
        <button type="button" onClick={() => execCommand('insertOrderedList')} className="p-2 rounded hover:bg-[#F5F5F0] text-[#4A4A4A] hover:text-[#1A1A1A]" title="Numbered List"><ListOrdered className="w-4 h-4" /></button>
        <button type="button" onClick={() => execCommand('formatBlock', 'blockquote')} className="p-2 rounded hover:bg-[#F5F5F0] text-[#4A4A4A] hover:text-[#1A1A1A]" title="Quote"><Quote className="w-4 h-4" /></button>
        <div className="w-px h-6 bg-white/10 mx-1" />
        <button type="button" onClick={() => { const url = prompt('Enter URL:'); if (url) execCommand('createLink', url); }} className="p-2 rounded hover:bg-[#F5F5F0] text-[#4A4A4A] hover:text-[#1A1A1A]" title="Link"><Link2 className="w-4 h-4" /></button>
        <button type="button" onClick={insertImage} className="p-2 rounded hover:bg-[#F5F5F0] text-[#4A4A4A] hover:text-[#1A1A1A]" title="Image"><Image className="w-4 h-4" /></button>
        <button type="button" onClick={insertEmbed} className="p-2 rounded hover:bg-[#F5F5F0] text-[#4A4A4A] hover:text-[#1A1A1A]" title="Embed Video/Link"><Code className="w-4 h-4" /></button>
        <button type="button" onClick={insertIframe} className="px-2 py-1 rounded hover:bg-[#F5F5F0] text-[#4A4A4A] hover:text-[#1A1A1A] text-[10px] font-bold border border-gray-200" title="Paste iframe / embed code (Twitter, YouTube, Instagram, FB)" data-testid="editor-iframe-btn">{'</>'}</button>
      </div>

      {/* Toolbar Row 1.5: Image placement */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-1.5 bg-gray-50 border-b border-gray-200 text-[11px]">
        <span className="text-[#737373] font-bold uppercase tracking-wider">Image</span>
        <div className="flex items-center gap-1" data-testid="editor-image-align">
          <button type="button" onClick={() => setImageAlignment('left')} className="px-2 py-0.5 rounded border border-gray-200 hover:border-[#D93025] text-gray-700">Left</button>
          <button type="button" onClick={() => setImageAlignment('center')} className="px-2 py-0.5 rounded border border-gray-200 hover:border-[#D93025] text-gray-700">Center</button>
          <button type="button" onClick={() => setImageAlignment('right')} className="px-2 py-0.5 rounded border border-gray-200 hover:border-[#D93025] text-gray-700">Right</button>
        </div>
        <div className="w-px h-4 bg-gray-200" />
        <span className="text-[#737373] font-bold uppercase tracking-wider">Size</span>
        <div className="flex items-center gap-1" data-testid="editor-image-size">
          <button type="button" onClick={() => setImageSize('small')} className="px-2 py-0.5 rounded border border-gray-200 hover:border-[#D93025] text-gray-700">S · 320px</button>
          <button type="button" onClick={() => setImageSize('medium')} className="px-2 py-0.5 rounded border border-gray-200 hover:border-[#D93025] text-gray-700">M · 600px</button>
          <button type="button" onClick={() => setImageSize('large')} className="px-2 py-0.5 rounded border border-gray-200 hover:border-[#D93025] text-gray-700">L · Full</button>
        </div>
        <span className="text-[10px] text-gray-400 ml-auto">Tip: Featured 1600×900, Inline 1200×675, Thumbnail 600×400</span>
      </div>

      {/* Toolbar Row 2: Font, Size, Line Height */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-1.5 bg-gray-50 border-b border-gray-200">
        {/* Font Selector */}
        <div className="flex items-center gap-1">
          <Type className="w-3.5 h-3.5 text-[#737373]" />
          <select value={selectedFont} onChange={(e) => applyFont(e.target.value)}
            className="bg-transparent text-gray-600 text-xs border border-gray-200 rounded px-2 py-1 outline-none" data-testid="font-selector">
            {FONT_OPTIONS.map(f => <option key={f.value} value={f.value} style={{ background: '#12121a' }}>{f.label}</option>)}
          </select>
        </div>

        <div className="w-px h-5 bg-white/10" />

        {/* Font Size with PX input */}
        <div className="flex items-center gap-1">
          <button onClick={() => applyFontSize(Math.max(8, fontSize - 1))} className="p-1 rounded hover:bg-[#F5F5F0] text-gray-400"><Minus className="w-3 h-3" /></button>
          <input type="number" value={fontSize} onChange={(e) => applyFontSize(Math.max(8, Math.min(72, parseInt(e.target.value) || 16)))}
            className="w-10 bg-transparent text-gray-800 text-xs text-center border border-gray-200 rounded px-1 py-0.5 outline-none" data-testid="font-size-input" />
          <span className="text-[#737373] text-xs">px</span>
          <button onClick={() => applyFontSize(Math.min(72, fontSize + 1))} className="p-1 rounded hover:bg-[#F5F5F0] text-gray-400"><Plus className="w-3 h-3" /></button>
        </div>

        <div className="w-px h-5 bg-white/10" />

        {/* Line Height */}
        <div className="flex items-center gap-1">
          <AlignLeft className="w-3.5 h-3.5 text-[#737373]" />
          <select value={lineHeight} onChange={(e) => applyLineHeight(parseFloat(e.target.value))}
            className="bg-transparent text-gray-600 text-xs border border-gray-200 rounded px-2 py-1 outline-none" data-testid="line-height-selector">
            {[1.0, 1.2, 1.4, 1.5, 1.6, 1.8, 2.0, 2.5].map(lh => <option key={lh} value={lh} style={{ background: '#12121a' }}>{lh}x</option>)}
          </select>
        </div>
      </div>

      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className="min-h-[300px] p-4 bg-white text-[#1a1a1a] focus:outline-none prose max-w-none font-kannada border-t border-gray-100"
        style={{ lineHeight: String(lineHeight), fontSize: `${fontSize}px`, fontFamily: selectedFont }}
        data-placeholder={placeholder}
        suppressContentEditableWarning
        data-testid="editor-content-area"
      />
      
      <style>{`
        [contenteditable]:empty:before { content: attr(data-placeholder); color: rgba(0,0,0,0.3); pointer-events: none; }
        [contenteditable] h1 { font-size: 1.5rem; font-weight: 700; margin: 1rem 0; }
        [contenteditable] h2 { font-size: 1.25rem; font-weight: 600; margin: 0.75rem 0; }
        [contenteditable] p { margin: 0.5rem 0; }
        [contenteditable] ul, [contenteditable] ol { padding-left: 1.5rem; margin: 0.5rem 0; }
        [contenteditable] blockquote { border-left: 3px solid #D4AF37; padding-left: 1rem; margin: 1rem 0; font-style: italic; color: rgba(255,255,255,0.7); }
        [contenteditable] a { color: #D4AF37; text-decoration: underline; }
        [contenteditable] img { max-width: 100%; height: auto; border-radius: 8px; margin: 1rem 0; }
      `}</style>
    </div>
  );
}

// --- Schedule Modal ---
function ScheduleModal({ isOpen, onClose, onSchedule }) {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-white mb-4">Schedule Publication</h3>
        <div className="space-y-4">
          <div>
            <Label className="text-gray-500 text-sm">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] mt-1" />
          </div>
          <div>
            <Label className="text-gray-500 text-sm">Time</Label>
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] mt-1" />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1 border-gray-200 text-gray-500">Cancel</Button>
          <Button onClick={() => { if (date && time) { onSchedule(new Date(`${date}T${time}`).toISOString()); onClose(); } }} className="flex-1 bg-palace-500 text-white">
            <Calendar className="w-4 h-4 mr-2" />Schedule
          </Button>
        </div>
      </div>
    </div>
  );
}

// --- Searchable Tag Select with use_count sort + auto-suggest ---
function TagSearchSelect({ allTags, selected, onToggle, onCreated, suggestText = '' }) {
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const q = query.trim().toLowerCase();

  // Auto-suggest: when user hasn't typed, surface tags whose name appears in
  // the article's title/summary so the most relevant options bubble up first.
  const suggested = useMemo(() => {
    if (!suggestText || q) return [];
    const text = suggestText.toLowerCase();
    return allTags.filter(t => {
      const en = (t.name || '').toLowerCase();
      const kn = (t.name_kn || '').toLowerCase();
      return (en && text.includes(en)) || (kn && suggestText.includes(t.name_kn || ''));
    }).slice(0, 8);
  }, [allTags, suggestText, q]);

  const matches = !q
    ? allTags.slice(0, 30)  // already sorted by use_count from backend
    : allTags.filter(t =>
        (t.name || '').toLowerCase().includes(q) ||
        (t.name_kn || '').toLowerCase().includes(q)
      ).slice(0, 30);
  const exactMatch = q
    ? allTags.some(t => (t.name || '').toLowerCase() === q || (t.name_kn || '').toLowerCase() === q)
    : true;
  const selectedTags = allTags.filter(t => selected.includes(t.tag_id));

  const createNow = async () => {
    if (!q || busy) return;
    const looksKannada = /[\u0c80-\u0cff]/.test(query);
    const payload = looksKannada ? { name: query, name_kn: query } : { name: query, name_kn: '' };
    setBusy(true);
    try {
      const res = await createTag(payload);
      onCreated?.(res.data);
      setQuery('');
    } catch (e) {
      alert('Failed to create tag: ' + (e?.response?.data?.detail || e?.message || ''));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="tag-search-select">
      {/* Selected chips */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {selectedTags.map(t => (
            <span key={t.tag_id} className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 text-xs rounded-lg bg-[#D93025] text-white" data-testid={`selected-tag-${t.tag_id}`}>
              {t.name_kn || t.name}
              <button type="button" onClick={() => onToggle(t.tag_id)} className="hover:bg-white/20 rounded p-0.5" aria-label="Remove">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {/* Suggested (when no query yet) */}
      {suggested.length > 0 && !q && (
        <div className="mb-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#D93025] mb-1.5">Suggested from article</p>
          <div className="flex flex-wrap gap-1.5">
            {suggested.map(t => {
              const isSel = selected.includes(t.tag_id);
              if (isSel) return null;
              return (
                <button key={t.tag_id} type="button" onClick={() => onToggle(t.tag_id)} className="px-2.5 py-1 text-xs rounded-lg border border-[#D93025]/30 bg-[#D93025]/5 text-[#D93025] hover:bg-[#D93025] hover:text-white transition-colors" data-testid={`tag-suggested-${t.tag_id}`}>
                  + {t.name_kn || t.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {/* Search input */}
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search tags or type a new one..."
        className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] text-sm"
        data-testid="tag-search-input"
        onKeyDown={(e) => { if (e.key === 'Enter' && q && !exactMatch) { e.preventDefault(); createNow(); } }}
      />
      {/* Dropdown */}
      <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-50">
        {!q && matches.length > 0 && (
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#737373] px-3 pt-2 pb-1">Most used</p>
        )}
        {matches.length === 0 && !q && (
          <p className="text-xs text-gray-400 px-3 py-3 text-center">No tags yet — type to create one.</p>
        )}
        {matches.map(t => {
          const isSel = selected.includes(t.tag_id);
          return (
            <button
              key={t.tag_id}
              type="button"
              onClick={() => onToggle(t.tag_id)}
              className={`w-full px-3 py-2 text-left text-xs flex items-center justify-between hover:bg-[#F5F5F0] transition-colors ${isSel ? 'bg-[#D93025]/5' : ''}`}
              data-testid={`tag-option-${t.tag_id}`}
            >
              <span className="font-kannada">{t.name_kn || t.name}{t.name_kn && t.name && t.name !== t.name_kn ? ` · ${t.name}` : ''}</span>
              <span className="flex items-center gap-1.5">
                {(t.use_count ?? 0) > 0 && <span className="text-[10px] text-gray-400">{t.use_count}</span>}
                {isSel && <Check className="w-3.5 h-3.5 text-[#D93025]" />}
              </span>
            </button>
          );
        })}
        {q && !exactMatch && (
          <button
            type="button"
            onClick={createNow}
            disabled={busy}
            className="w-full px-3 py-2 text-left text-xs flex items-center justify-between bg-[#D93025]/5 hover:bg-[#D93025]/10 text-[#D93025] font-bold transition-colors"
            data-testid="tag-create-inline"
          >
            <span className="flex items-center gap-1.5">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
              Create tag: &quot;{query}&quot;
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

// --- Main Editor Component ---
export default function EditorArticleWriter() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');

  const [form, setForm] = useState({
    title: '', title_kn: '', content: '', content_kn: '',
    summary: '', summary_kn: '', category_id: '', category_ids: [], tags: [],
    featured_image: '', photo_caption: '', status: 'draft', featured: false,
    scheduled_at: null, expires_at: '', author_label: 'ತಾಯ್ನಾಡು ಸುದ್ದಿ ಮನೆ',
    meta_title: '', meta_description: '', custom_slug: '',
    show_on_homepage: true, show_in_category: true, is_featured_banner: false
  });
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState('');
  const [preview, setPreview] = useState(false);
  const [articleId, setArticleId] = useState(editId);
  const [lastSaved, setLastSaved] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const [error, setError] = useState('');
  const autoSaveRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !['admin', 'editor', 'author'].includes(user.role)) {
      navigate('/');
      return;
    }
    Promise.all([getCategories(), getTags()]).then(([catRes, tagRes]) => {
      setCategories(catRes.data || []);
      setAllTags(tagRes.data || []);
    }).catch(err => setError('Failed to load categories/tags'));
    if (editId) {
      getArticle(editId).then(res => {
        const a = res.data;
        setForm({
          title: a.title || '', title_kn: a.title_kn || '',
          content: a.content || '', content_kn: a.content_kn || '',
          summary: a.summary || '', summary_kn: a.summary_kn || '',
          category_id: a.category_id || '',
          category_ids: Array.isArray(a.category_ids) && a.category_ids.length ? a.category_ids : (a.category_id ? [a.category_id] : []),
          tags: a.tags || [],
          featured_image: a.featured_image || '', photo_caption: a.photo_caption || '',
          status: a.status || 'draft', featured: a.featured || false,
          scheduled_at: a.scheduled_at, expires_at: a.expires_at || '',
          author_label: a.author_label || 'ತಾಯ್ನಾಡು ಸುದ್ದಿ ಮನೆ',
          meta_title: a.meta_title || '', meta_description: a.meta_description || '',
          custom_slug: a.custom_slug || a.slug || '',
          show_on_homepage: a.show_on_homepage !== false,
          show_in_category: a.show_in_category !== false,
          is_featured_banner: a.is_featured_banner || false
        });
        setArticleId(editId);
      }).catch(err => setError('Failed to load article'));
    }
  }, [user, authLoading, navigate, editId]);

  // Auto-save
  useEffect(() => {
    if (!articleId || form.status === 'published') return;
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(async () => {
      if (form.title || form.content) {
        setAutoSaveStatus('saving');
        try {
          await autoSaveArticle(articleId, {
            title: form.title, content: form.content,
            title_kn: form.title_kn, content_kn: form.content_kn,
            summary: form.summary, featured_image: form.featured_image
          });
          setLastSaved(new Date());
          setAutoSaveStatus('saved');
          setTimeout(() => setAutoSaveStatus(''), 2000);
        } catch { setAutoSaveStatus('error'); }
      }
    }, 3000);
    return () => { if (autoSaveRef.current) clearTimeout(autoSaveRef.current); };
  }, [form.title, form.content, form.title_kn, form.content_kn, form.summary, form.featured_image, articleId, form.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Create draft on first load
  useEffect(() => {
    if (!editId && user && !articleId) {
      createDraft().then(res => setArticleId(res.data.article_id)).catch(err => setError('Failed to create draft'));
    }
  }, [editId, user, articleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (status) => {
    setSaving(true);
    setError('');
    try {
      const data = { ...form, status };
      if (articleId) {
        await updateArticle(articleId, data);
      } else {
        const res = await createArticle(data);
        setArticleId(res.data.article_id);
      }
      navigate(user.role === 'admin' ? '/admin' : '/editor');
    } catch (err) {
      setError('Failed to save article. Please try again.');
      console.error('Save error:', err);
    } finally { setSaving(false); }
  };

  const handleSchedule = async (scheduledAt) => {
    setSaving(true);
    try {
      const data = { ...form, scheduled_at: scheduledAt };
      if (articleId) {
        await updateArticle(articleId, data);
        await scheduleArticle(articleId, scheduledAt);
      }
      navigate(user.role === 'admin' ? '/admin' : '/editor');
    } catch (err) { setError('Failed to schedule article'); }
    finally { setSaving(false); }
  };

  const handleAISummarize = async () => {
    if (!form.content) return;
    setAiLoading('summarize');
    try {
      const plain = form.content.replace(/<[^>]*>/g, '');
      const res = await aiSummarize(plain);
      // Limit to 20 words
      const words = (res.data.result || '').split(/\s+/).slice(0, 20).join(' ');
      setForm(p => ({ ...p, summary: words }));
    } catch { setError('AI summarize failed'); }
    finally { setAiLoading(''); }
  };

  const handleAITranslate = async (field, targetLang) => {
    const sourceField = targetLang === 'kn' ? field : field.replace('_kn', '');
    const sourceText = form[sourceField];
    if (!sourceText) return;
    setAiLoading(`translate-${field}`);
    try {
      const plain = sourceText.replace(/<[^>]*>/g, '');
      const res = await aiTranslate(plain, targetLang);
      const targetField = targetLang === 'kn' ? `${field}_kn` : field.replace('_kn', '');
      setForm(p => ({ ...p, [targetField]: res.data.result }));
    } catch { setError('AI translate failed'); }
    finally { setAiLoading(''); }
  };

  const toggleTag = (tagId) => {
    setForm(p => ({ ...p, tags: p.tags.includes(tagId) ? p.tags.filter(t => t !== tagId) : [...p.tags, tagId] }));
  };

  const handleSummaryChange = (e) => {
    const text = e.target.value;
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length <= 20) {
      setForm(p => ({ ...p, summary: text }));
    }
  };

  const summaryWordCount = form.summary.split(/\s+/).filter(Boolean).length;

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-red-500/30 border-t-palace-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (preview) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] py-8">
        <div className="max-w-4xl mx-auto px-4" data-testid="article-preview">
          <Button variant="ghost" onClick={() => setPreview(false)} className="mb-6 text-gray-500 hover:text-[#1A1A1A]">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Editor
          </Button>
          {form.featured_image && (
            <div>
              <img src={form.featured_image} alt="" className="w-full h-72 object-cover rounded-2xl mb-2" onError={(e) => { e.target.style.display='none'; }} />
              {form.photo_caption && <p className="text-xs text-[#737373] italic mb-6">{form.photo_caption}</p>}
            </div>
          )}
          <h1 className="text-4xl font-heading font-bold text-[#1A1A1A] mb-4 font-kannada">{form.title_kn || form.title || 'Untitled'}</h1>
          {form.summary && (
            <p className="text-base text-gray-500 mb-8 italic border-l-4 border-red-500 pl-4" style={{ fontFamily: "'Baloo Tamma 2', sans-serif", fontSize: '16px' }}>{form.summary}</p>
          )}
          <p className="text-sm text-[#737373] mb-6">{form.author_label} | {new Date().toLocaleDateString('kn-IN')}</p>
          <div className="prose max-w-none font-kannada text-[#1A1A1A]" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(form.content || '<p>No content yet...</p>') }} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="editor-article-writer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Error Banner */}
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-2 text-red-400 text-sm" data-testid="editor-error">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)} size="sm" className="text-gray-500 hover:text-[#1A1A1A]" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-heading font-bold text-[#1A1A1A]">{editId ? 'Edit Article' : 'Create New Article'}</h1>
              <div className="flex items-center gap-3 mt-1">
                {autoSaveStatus === 'saving' && <span className="text-xs text-[#737373] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Saving...</span>}
                {autoSaveStatus === 'saved' && <span className="text-xs text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" />Auto-saved</span>}
                {autoSaveStatus === 'error' && <span className="text-xs text-red-400 flex items-center gap-1"><AlertCircle className="w-3 h-3" />Save failed</span>}
                {lastSaved && <span className="text-xs text-[#737373]">Last saved {lastSaved.toLocaleTimeString()}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setPreview(true)} className="border-gray-200 text-gray-500 hover:text-[#1A1A1A]" data-testid="preview-btn">
              <Eye className="w-4 h-4 mr-2" />Preview
            </Button>
            {form.status === 'published' ? (
              <Button onClick={() => { if (window.confirm('Unpublish?')) { unpublishArticle(articleId).then(() => setForm(p => ({ ...p, status: 'draft' }))); } }}
                variant="outline" className="border-amber-500/50 text-amber-400">Unpublish</Button>
            ) : (
              <>
                <Button onClick={() => handleSave('draft')} disabled={saving} variant="outline" className="border-gray-200 text-gray-500" data-testid="save-draft-btn">
                  <Save className="w-4 h-4 mr-2" />Draft
                </Button>
                <Button onClick={() => setShowScheduleModal(true)} disabled={saving} variant="outline" className="border-purple-500/50 text-purple-400">
                  <Calendar className="w-4 h-4 mr-2" />Schedule
                </Button>
              </>
            )}
            <Button onClick={() => handleSave('published')} disabled={saving} className="bg-gradient-to-r from-palace-500 to-silk-500 text-white" data-testid="publish-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Publish
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-gray-50 border border-gray-200">
            <TabsTrigger value="content" className="data-[state=active]:bg-palace-500"><FileText className="w-4 h-4 mr-2" />Content</TabsTrigger>
            <TabsTrigger value="media" className="data-[state=active]:bg-palace-500"><Image className="w-4 h-4 mr-2" />Media</TabsTrigger>
            <TabsTrigger value="seo" className="data-[state=active]:bg-palace-500"><Globe className="w-4 h-4 mr-2" />SEO</TabsTrigger>
          </TabsList>

          <TabsContent value="content" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                {/* Kannada Title (Primary) */}
                <div className="rounded-2xl bg-white border border-white/5 p-6">
                  <Label className="text-xs font-medium text-[#737373] uppercase tracking-wider mb-2 block">ಶೀರ್ಷಿಕೆ (Kannada) *</Label>
                  <Input value={form.title_kn} onChange={(e) => setForm(p => ({...p, title_kn: e.target.value}))}
                    placeholder="ಲೇಖನದ ಶೀರ್ಷಿಕೆ ನಮೂದಿಸಿ" className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] text-lg font-kannada placeholder:text-[#737373] h-12" data-testid="title-kn-input" />
                </div>

                {/* English Title */}
                <div className="rounded-2xl bg-white border border-white/5 p-6">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-medium text-[#737373] uppercase tracking-wider">Title (English)</Label>
                    <Button size="sm" variant="ghost" onClick={() => handleAITranslate('title_kn', 'en')} disabled={!!aiLoading} className="text-xs text-[#D93025]">
                      {aiLoading === 'translate-title' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Languages className="w-3 h-3 mr-1" />}AI Translate
                    </Button>
                  </div>
                  <Input value={form.title} onChange={(e) => setForm(p => ({...p, title: e.target.value}))}
                    placeholder="Article title" className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] text-lg font-heading placeholder:text-[#737373] h-12" data-testid="title-en-input" />
                </div>

                {/* Content Editor */}
                <div className="rounded-2xl bg-white border border-white/5 p-6">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-xs font-medium text-[#737373] uppercase tracking-wider">ವಿಷಯ (Content)</Label>
                    <Button size="sm" variant="ghost" onClick={() => setShowMediaLibrary(true)} className="text-xs text-[#D93025]">
                      <ImagePlus className="w-3 h-3 mr-1" />Insert from Media
                    </Button>
                  </div>
                  <RichTextEditor value={form.content} onChange={(val) => setForm(p => ({...p, content: val}))}
                    placeholder="ಇಲ್ಲಿ ಲೇಖನದ ವಿಷಯವನ್ನು ಬರೆಯಿರಿ..." onImageInsert={() => setShowMediaLibrary(true)} />
                </div>

                {/* Summary (20-word limit, Baloo Tamma 2 default) */}
                <div className="rounded-2xl bg-white border border-white/5 p-6">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-xs font-medium text-[#737373] uppercase tracking-wider">ಸಾರಾಂಶ (Summary) - Max 20 words</Label>
                    <Button size="sm" variant="ghost" onClick={handleAISummarize} disabled={!!aiLoading} className="text-xs text-[#D93025]">
                      {aiLoading === 'summarize' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}AI Summarize
                    </Button>
                  </div>
                  <Textarea value={form.summary} onChange={handleSummaryChange}
                    placeholder="Brief summary (max 20 words)"
                    className="min-h-[80px] bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] placeholder:text-[#737373]"
                    style={{ fontFamily: "'Baloo Tamma 2', sans-serif", fontSize: '16px' }}
                    data-testid="summary-input" />
                  <p className={`text-xs mt-2 ${summaryWordCount > 18 ? 'text-amber-400' : 'text-[#737373]'}`}>
                    {summaryWordCount}/20 words
                  </p>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Categories (multi-select) — prominent at top so author picks first */}
                <div className="rounded-2xl bg-white border border-gray-200 p-6 shadow-sm ring-1 ring-red-500/10">
                  <Label className="text-xs font-bold text-[#D93025] uppercase tracking-wider mb-1 block">
                    Categories *
                  </Label>
                  <p className="text-[11px] text-[#737373] mb-3">Click one or more. The first one clicked is the primary. Article will appear under every selected category.</p>
                  <div className="flex flex-wrap gap-2" data-testid="category-multiselect">
                    {categories.map(cat => {
                      const selected = (form.category_ids || []).includes(cat.category_id);
                      const isPrimary = selected && form.category_ids[0] === cat.category_id;
                      return (
                        <button
                          type="button"
                          key={cat.category_id}
                          onClick={() => {
                            setForm(p => {
                              const ids = Array.isArray(p.category_ids) ? [...p.category_ids] : [];
                              const i = ids.indexOf(cat.category_id);
                              if (i >= 0) ids.splice(i, 1); else ids.push(cat.category_id);
                              return { ...p, category_ids: ids, category_id: ids[0] || '' };
                            });
                          }}
                          className={`px-3 py-1.5 text-xs rounded-lg border transition-all flex items-center gap-1.5 font-kannada ${
                            selected
                              ? 'bg-[#D93025] text-white border-[#D93025] shadow'
                              : 'bg-white border-gray-200 text-gray-700 hover:border-[#D93025]/50'
                          }`}
                          data-testid={`cat-chip-${cat.category_id}`}
                        >
                          {isPrimary && <span className="w-1.5 h-1.5 rounded-full bg-white" title="Primary" />}
                          {cat.name_kn || cat.name}
                        </button>
                      );
                    })}
                  </div>
                  {(form.category_ids || []).length > 0 && (
                    <p className="text-[11px] text-[#737373] mt-3">
                      Primary: <span className="text-[#1A1A1A] font-semibold font-kannada">
                        {(() => { const c = categories.find(c => c.category_id === form.category_ids[0]); return c ? (c.name_kn || c.name) : '—'; })()}
                      </span>
                      {form.category_ids.length > 1 && (
                        <> · Also in <span className="text-[#1A1A1A] font-semibold">{form.category_ids.length - 1}</span> more</>
                      )}
                    </p>
                  )}
                </div>

                {/* Status */}
                <div className="rounded-2xl bg-white border border-white/5 p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Status</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      form.status === 'published' ? 'bg-emerald-500/20 text-emerald-400' :
                      form.status === 'scheduled' ? 'bg-purple-500/20 text-purple-400' : 'bg-amber-500/20 text-amber-400'
                    }`}>{form.status.charAt(0).toUpperCase() + form.status.slice(1)}</span>
                  </div>
                </div>

                {/* Author Label */}
                <div className="rounded-2xl bg-white border border-white/5 p-6">
                  <Label className="text-xs font-medium text-[#737373] uppercase tracking-wider mb-3 block">Author Label</Label>
                  <Input value={form.author_label} onChange={(e) => setForm(p => ({...p, author_label: e.target.value}))}
                    placeholder="Taaynadu News Desk" className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] placeholder:text-[#737373]" data-testid="author-label-input" />
                </div>

                {/* News Expiry */}
                <div className="rounded-2xl bg-white border border-white/5 p-6">
                  <Label className="text-xs font-medium text-[#737373] uppercase tracking-wider mb-3 block">News Expiry</Label>
                  <Input type="datetime-local" value={form.expires_at ? form.expires_at.slice(0, 16) : ''}
                    onChange={(e) => setForm(p => ({...p, expires_at: e.target.value ? new Date(e.target.value).toISOString() : ''}))}
                    className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A]" data-testid="expires-at-input" />
                  <p className="text-xs text-[#737373] mt-1">Auto-hides after this date. Publish date is set automatically when you publish.</p>
                </div>

                {/* Tags */}
                <div className="rounded-2xl bg-white border border-white/5 p-6">
                  <Label className="text-xs font-medium text-[#737373] uppercase tracking-wider mb-3 block">Tags</Label>
                  <TagSearchSelect
                    allTags={allTags}
                    selected={form.tags}
                    onToggle={toggleTag}
                    suggestText={`${form.title || ''} ${form.title_kn || ''} ${form.summary || ''} ${form.summary_kn || ''}`}
                    onCreated={(tag) => { setAllTags(prev => [...prev, tag]); setForm(p => ({ ...p, tags: [...(p.tags || []), tag.tag_id] })); }}
                  />
                </div>

                {/* Content Placement */}
                <div className="rounded-2xl bg-white border border-white/5 p-6 space-y-4">
                  <Label className="text-xs font-medium text-[#737373] uppercase tracking-wider block">Content Placement</Label>
                  {[
                    ['show_on_homepage', 'Show on Homepage'],
                    ['show_in_category', 'Show in Category'],
                    ['is_featured_banner', 'Featured Banner'],
                    ['featured', 'Featured Article'],
                  ].map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{label}</span>
                      <Switch checked={form[key]} onCheckedChange={(val) => setForm(p => ({...p, [key]: val}))} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="media" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="rounded-2xl bg-white border border-white/5 p-6">
                <Label className="text-xs font-medium text-[#737373] uppercase tracking-wider mb-4 block">Featured Image</Label>
                <ImageUploader currentImage={form.featured_image}
                  onUpload={(url) => setForm(p => ({...p, featured_image: url}))}
                  onRemove={() => setForm(p => ({...p, featured_image: ''}))}
                  captionValue={form.photo_caption}
                  onCaptionChange={(val) => setForm(p => ({...p, photo_caption: val}))} />
                <p className="text-xs text-[#737373] mt-3">Or enter URL:</p>
                <Input value={form.featured_image} onChange={(e) => setForm(p => ({...p, featured_image: e.target.value}))}
                  placeholder="https://example.com/image.jpg" className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] placeholder:text-[#737373] mt-2" />
              </div>
              <div className="rounded-2xl bg-white border border-white/5 p-6">
                <Label className="text-xs font-medium text-[#737373] uppercase tracking-wider mb-4 block">Media Library</Label>
                <Button onClick={() => setShowMediaLibrary(true)} variant="outline" className="w-full h-32 border-dashed border-white/20 text-gray-500">
                  <div className="flex flex-col items-center gap-2">
                    <Image className="w-8 h-8" /><span>Browse Media Library</span>
                  </div>
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="seo" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                <div className="rounded-2xl bg-white border border-white/5 p-6">
                  <Label className="text-xs font-medium text-[#737373] uppercase tracking-wider mb-2 block">Meta Title</Label>
                  <Input value={form.meta_title} onChange={(e) => setForm(p => ({...p, meta_title: e.target.value}))}
                    placeholder={form.title || "SEO title"} className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] placeholder:text-[#737373]" />
                  <p className="text-xs text-[#737373] mt-2">{(form.meta_title || form.title || '').length}/60</p>
                </div>
                <div className="rounded-2xl bg-white border border-white/5 p-6">
                  <Label className="text-xs font-medium text-[#737373] uppercase tracking-wider mb-2 block">Meta Description</Label>
                  <Textarea value={form.meta_description} onChange={(e) => setForm(p => ({...p, meta_description: e.target.value}))}
                    placeholder={form.summary || "SEO description"} className="min-h-[100px] bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] placeholder:text-[#737373]" />
                  <p className="text-xs text-[#737373] mt-2">{(form.meta_description || form.summary || '').length}/160</p>
                </div>
                <div className="rounded-2xl bg-white border border-white/5 p-6">
                  <Label className="text-xs font-medium text-[#737373] uppercase tracking-wider mb-2 block">URL Slug</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-[#737373] text-sm">/article/</span>
                    <Input value={form.custom_slug} onChange={(e) => setForm(p => ({...p, custom_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')}))}
                      placeholder="custom-url-slug" className="bg-[#F5F5F0] border-[#EAEAE8] text-[#1A1A1A] placeholder:text-[#737373]" />
                  </div>
                </div>
              </div>
              <div className="rounded-2xl bg-white border border-white/5 p-6">
                <Label className="text-xs font-medium text-[#737373] uppercase tracking-wider mb-4 block">Search Preview</Label>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-blue-600 text-lg hover:underline">{form.meta_title || form.title || 'Article Title'}</p>
                  <p className="text-green-700 text-sm">taaynadu.news/article/{form.custom_slug || 'slug'}</p>
                  <p className="text-gray-600 text-sm mt-1">{(form.meta_description || form.summary || 'Description...').substring(0, 160)}</p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <ScheduleModal isOpen={showScheduleModal} onClose={() => setShowScheduleModal(false)} onSchedule={handleSchedule} />
      <MediaLibraryModal isOpen={showMediaLibrary} onClose={() => setShowMediaLibrary(false)}
        onSelect={(urlOrUrls) => {
          const urls = Array.isArray(urlOrUrls) ? urlOrUrls : [urlOrUrls];
          if (activeTab === 'media') {
            // For featured image only use the first one
            setForm(p => ({ ...p, featured_image: urls[0] }));
          } else {
            // Body editor: insert all images
            urls.forEach(url => document.execCommand('insertImage', false, url));
          }
        }} />
    </div>
  );
}
